import { FC } from 'react';
import { useLocation } from 'react-router';
import { Flex, List, ListItem } from '@chakra-ui/react';

import Heading from 'components/_core/Heading';
import Text from 'components/_core/Text';
import Timeline from 'components/_core/Timeline';
import * as Layout from 'layouts/OfferDetailsLayout';

import ActivateInsuranceButton from '../../components/ActivateInsuranceButton';
import AsideOfferCard from '../../components/AsideOfferCard';
import AskQuestionIntercomButton from '../../components/AskQuestionIntercomButton';
import ClaimIntercomButton from '../../components/ClaimIntercomButton';
import OfferListIcon from '../../components/OfferListIcon';
import SimilarOffers from '../../components/SimilarOffers';
import useInsuranceQuery from '../../hooks/useInsuranceCoverage';
import offers from '../../offers';
import * as routes from '../../routes';
import { AssuredBy, OfferDetailsFromSource } from '../../types';
import cover from './assets/cover.png';
import locales from './locales';

const OFFER_NAME = 'Computer Breakage';

interface RouteState {
  from?: OfferDetailsFromSource;
}

const ComputerBreakageOffer: FC = () => {
  const { state: routeState } = useLocation<RouteState>();
  const { coverage } = useInsuranceQuery();

  const isCovered = coverage.computerBreakage;

  return (
    <Layout.PageContainer
      trackViewProps={{
        eventProperties: {
          from: routeState?.from,
          offer: OFFER_NAME,
        },
        pageName: 'InsuranceOfferDetails Viewed',
        usePrefix: false,
      }}
    >
      <Layout.BackButton to={routes.coverage} />

      <Layout.StickyHeader>
        <Layout.HeaderTitle>{locales.title}</Layout.HeaderTitle>

        {isCovered ? (
          <ClaimIntercomButton
            intercomMessage={locales.claimButton.intercomMessage}
            offerName={OFFER_NAME}
          />
        ) : (
          <ActivateInsuranceButton offerName={OFFER_NAME} />
        )}
      </Layout.StickyHeader>

      <Layout.Cover ratio={5.2} src={cover} />

      <Layout.ContentWrapper
        aside={
          <Layout.Aside>
            <AsideOfferCard
              assuredBy={AssuredBy.MutuaideAssistance}
              offer={offers.computerBreakage}
            />
            <AskQuestionIntercomButton offer={OFFER_NAME} />
          </Layout.Aside>
        }
      >
        <Heading paddingBottom="space-16">
          {locales.content.include.title}
        </Heading>

        <List paddingBottom="space-40" spacing="space-16">
          {locales.content.include.items.map((text) => (
            <ListItem alignItems="baseline" as={Flex} key={text}>
              <OfferListIcon color="lightBlue.0" name="tick" />
              <Text>{text}</Text>
            </ListItem>
          ))}
        </List>

        <Heading paddingBottom="space-16">
          {locales.content.exclude.title}
        </Heading>

        <List paddingBottom="space-40" spacing="space-16">
          {locales.content.exclude.items.map((text) => (
            <ListItem alignItems="baseline" as={Flex} key={text}>
              <OfferListIcon color="red.0" name="cross" />
              <Text>{text}</Text>
            </ListItem>
          ))}
        </List>

        <Heading paddingBottom="space-16">
          {locales.content.timeline.title}
        </Heading>

        <Timeline>
          {locales.content.timeline.items.map((text) => (
            <Timeline.Step key={text}>{text}</Timeline.Step>
          ))}
        </Timeline>
      </Layout.ContentWrapper>

      <SimilarOffers
        offers={[
          offers.legalAssistance,
          offers.mobileScreen,
          offers.warrantyExtension,
        ]}
      />
    </Layout.PageContainer>
  );
};

export default ComputerBreakageOffer;
